// ─── ConfirmModal — modal de confirmación centrado (0615+) ───────────────────
// Reemplaza window.confirm/window.prompt. Centrado en pantalla, con overlay,
// soporta input opcional para motivo (rechazar) y variante peligrosa (rojo).
//
// Uso:
//   <ConfirmModal
//     open={modalOpen}
//     title="Aprobar versión"
//     message="¿Confirmas aprobar Edición 3?"
//     confirmLabel="Aprobar"
//     variant="primary" // 'primary' | 'success' | 'danger'
//     onConfirm={(motivo?) => { ... }}
//     onCancel={() => setOpen(false)}
//   />
//
//   // Con motivo (rechazar):
//   <ConfirmModal
//     open={...}
//     title="Rechazar"
//     message="..."
//     confirmLabel="Rechazar"
//     variant="danger"
//     requireMotivo
//     motivoLabel="Motivo del rechazo"
//     ...
//   />

import { useEffect, useRef, useState } from 'react';
import { useI18n } from '../../../i18n';

export type ConfirmVariant = 'primary' | 'success' | 'danger';

export interface ConfirmModalProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
  /** Si true, requiere capturar un motivo antes de confirmar. */
  requireMotivo?: boolean;
  motivoLabel?: string;
  motivoPlaceholder?: string;
  /**
   * 0665: si true, requiere capturar `asunto` (≥5 chars) + `alcance` (≥20 chars)
   * antes de confirmar. Cross-artefacto canónico para ENVIAR_REVISION.
   * Mutuamente exclusivo con `requireMotivo`.
   */
  requireAsuntoAlcance?: boolean;
  asuntoLabel?: string;
  asuntoPlaceholder?: string;
  alcanceLabel?: string;
  alcancePlaceholder?: string;
  /** Si true, el botón confirm queda en loading (no clickable). */
  loading?: boolean;
  /** Legacy: motivo es opcional (solo viene si requireMotivo=true). */
  onConfirm: (motivo?: string) => void;
  /**
   * 0665: callback alternativo cuando requireAsuntoAlcance=true.
   * Si está presente, sustituye a onConfirm en ese modo.
   */
  onConfirmAsuntoAlcance?: (payload: { asunto: string; alcance: string }) => void;
  onCancel: () => void;
}

const VARIANT_STYLES: Record<ConfirmVariant, { bg: string; hover: string }> = {
  primary: { bg: '#2F5496', hover: '#1F3864' },
  success: { bg: '#16A34A', hover: '#15803D' },
  danger: { bg: '#DC2626', hover: '#B91C1C' },
};

export function ConfirmModal({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel = 'Cancelar',
  variant = 'primary',
  requireMotivo,
  motivoLabel = 'Motivo',
  motivoPlaceholder,
  requireAsuntoAlcance,
  asuntoLabel,
  asuntoPlaceholder,
  alcanceLabel,
  alcancePlaceholder,
  loading,
  onConfirm,
  onConfirmAsuntoAlcance,
  onCancel,
}: ConfirmModalProps) {
  const { t } = useI18n();
  const [motivo, setMotivo] = useState('');
  const [motivoError, setMotivoError] = useState(false);
  // 0665: estado para modo asunto+alcance.
  const [asunto, setAsunto] = useState('');
  const [alcance, setAlcance] = useState('');
  const [asuntoError, setAsuntoError] = useState<string | null>(null);
  const [alcanceError, setAlcanceError] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const asuntoRef = useRef<HTMLInputElement>(null);
  const alcanceRef = useRef<HTMLTextAreaElement>(null);
  const cancelBtnRef = useRef<HTMLButtonElement>(null);

  // Reset state al abrir/cerrar.
  useEffect(() => {
    if (open) {
      setMotivo('');
      setMotivoError(false);
      setAsunto('');
      setAlcance('');
      setAsuntoError(null);
      setAlcanceError(null);
      // Focus inicial: input según modo.
      const timeoutId = setTimeout(() => {
        if (requireAsuntoAlcance) asuntoRef.current?.focus();
        else if (requireMotivo) inputRef.current?.focus();
        else cancelBtnRef.current?.focus();
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [open, requireMotivo, requireAsuntoAlcance]);

  // Cerrar con Escape.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) onCancel();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, loading, onCancel]);

  if (!open) return null;

  const handleConfirm = () => {
    if (requireAsuntoAlcance) {
      const asuntoTrim = asunto.trim();
      const alcanceTrim = alcance.trim();
      let hasError = false;
      if (asuntoTrim.length < 5) {
        setAsuntoError(t.workflow.asuntoMinError);
        asuntoRef.current?.focus();
        hasError = true;
      }
      if (alcanceTrim.length < 20) {
        setAlcanceError(t.workflow.alcanceMinError);
        if (!hasError) alcanceRef.current?.focus();
        hasError = true;
      }
      if (hasError) return;
      onConfirmAsuntoAlcance?.({ asunto: asuntoTrim, alcance: alcanceTrim });
      return;
    }
    if (requireMotivo && !motivo.trim()) {
      setMotivoError(true);
      inputRef.current?.focus();
      return;
    }
    onConfirm(requireMotivo ? motivo.trim() : undefined);
  };

  const variantStyle = VARIANT_STYLES[variant];

  return (
    <>
      {/* Overlay */}
      <div
        onClick={() => !loading && onCancel()}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.45)',
          zIndex: 1000,
          animation: 'via-overlay-fade-in 120ms ease-out',
        }}
      />

      {/* Modal centrado */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(440px, calc(100vw - 32px))',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 20px 60px rgba(15,23,42,0.30)',
          zIndex: 1001,
          fontFamily: 'inherit',
          animation: 'via-modal-pop 160ms ease-out',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '18px 22px 12px',
            borderBottom: '1px solid #F1F5F9',
          }}
        >
          <h3
            id="confirm-modal-title"
            style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 700,
              color: '#1F3864',
              fontFamily: 'inherit',
            }}
          >
            {title}
          </h3>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 22px' }}>
          <p
            style={{
              margin: 0,
              fontSize: 13,
              color: '#4A5568',
              lineHeight: 1.5,
              fontFamily: 'inherit',
            }}
          >
            {message}
          </p>

          {requireMotivo && !requireAsuntoAlcance && (
            <div style={{ marginTop: 14 }}>
              <label
                style={{
                  display: 'block',
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#1F3864',
                  marginBottom: 6,
                  fontFamily: 'inherit',
                }}
              >
                {motivoLabel} <span style={{ color: '#DC2626' }}>*</span>
              </label>
              <textarea
                ref={inputRef}
                value={motivo}
                onChange={(e) => {
                  setMotivo(e.target.value);
                  if (motivoError && e.target.value.trim()) setMotivoError(false);
                }}
                placeholder={motivoPlaceholder ?? ''}
                rows={3}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  fontSize: 13,
                  borderRadius: 6,
                  border: `1px solid ${motivoError ? '#DC2626' : '#CBD5E0'}`,
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  minHeight: 60,
                  outline: 'none',
                  background: loading ? '#F8FAFC' : '#fff',
                }}
              />
              {motivoError && (
                <span
                  style={{
                    display: 'block',
                    marginTop: 4,
                    fontSize: 11,
                    color: '#DC2626',
                    fontFamily: 'inherit',
                  }}
                >
                  {t.workflow.motivoRechazoRequerido}
                </span>
              )}
            </div>
          )}

          {/* 0665: bloque asunto+alcance cross-artefacto (ENVIAR_REVISION). */}
          {requireAsuntoAlcance && (
            <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label
                  htmlFor="confirm-modal-asunto"
                  style={{
                    display: 'block',
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#1F3864',
                    marginBottom: 6,
                    fontFamily: 'inherit',
                  }}
                >
                  {asuntoLabel ?? t.workflow.asuntoLabel}{' '}
                  <span style={{ color: '#DC2626' }}>*</span>
                </label>
                <input
                  id="confirm-modal-asunto"
                  ref={asuntoRef}
                  type="text"
                  value={asunto}
                  maxLength={300}
                  onChange={(e) => {
                    setAsunto(e.target.value);
                    if (asuntoError && e.target.value.trim().length >= 5) setAsuntoError(null);
                  }}
                  placeholder={asuntoPlaceholder ?? t.workflow.asuntoPlaceholder}
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    fontSize: 13,
                    borderRadius: 6,
                    border: `1px solid ${asuntoError ? '#DC2626' : '#CBD5E0'}`,
                    fontFamily: 'inherit',
                    outline: 'none',
                    background: loading ? '#F8FAFC' : '#fff',
                  }}
                />
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: 4,
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      color: asuntoError ? '#DC2626' : '#9CA3AF',
                      fontFamily: 'inherit',
                    }}
                  >
                    {asuntoError ?? t.workflow.asuntoHint}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      color: '#9CA3AF',
                      fontFamily: 'inherit',
                    }}
                  >
                    {asunto.length}/300
                  </span>
                </div>
              </div>
              <div>
                <label
                  htmlFor="confirm-modal-alcance"
                  style={{
                    display: 'block',
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#1F3864',
                    marginBottom: 6,
                    fontFamily: 'inherit',
                  }}
                >
                  {alcanceLabel ?? t.workflow.alcanceLabel}{' '}
                  <span style={{ color: '#DC2626' }}>*</span>
                </label>
                <textarea
                  id="confirm-modal-alcance"
                  ref={alcanceRef}
                  value={alcance}
                  onChange={(e) => {
                    setAlcance(e.target.value);
                    if (alcanceError && e.target.value.trim().length >= 20) setAlcanceError(null);
                  }}
                  placeholder={alcancePlaceholder ?? t.workflow.alcancePlaceholder}
                  rows={5}
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    fontSize: 13,
                    borderRadius: 6,
                    border: `1px solid ${alcanceError ? '#DC2626' : '#CBD5E0'}`,
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    minHeight: 100,
                    outline: 'none',
                    background: loading ? '#F8FAFC' : '#fff',
                  }}
                />
                <span
                  style={{
                    display: 'block',
                    marginTop: 4,
                    fontSize: 11,
                    color: alcanceError ? '#DC2626' : '#9CA3AF',
                    fontFamily: 'inherit',
                  }}
                >
                  {alcanceError ?? t.workflow.alcanceHint}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '12px 22px 18px',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={onCancel}
            disabled={loading}
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 600,
              color: '#4A5568',
              background: '#fff',
              border: '1px solid #CBD5E0',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              opacity: loading ? 0.6 : 1,
            }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={loading}
            style={{
              padding: '8px 18px',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 600,
              color: '#fff',
              background: variantStyle.bg,
              border: 'none',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              opacity: loading ? 0.6 : 1,
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => {
              if (!loading) (e.currentTarget as HTMLElement).style.background = variantStyle.hover;
            }}
            onMouseLeave={(e) => {
              if (!loading) (e.currentTarget as HTMLElement).style.background = variantStyle.bg;
            }}
          >
            {loading ? '…' : confirmLabel}
          </button>
        </div>
      </div>

      {/* Animaciones globales (inline para evitar dependencia de CSS module) */}
      <style>{`
        @keyframes via-overlay-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes via-modal-pop {
          0%   { opacity: 0; transform: translate(-50%, -50%) scale(0.96); }
          100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
      `}</style>
    </>
  );
}
